import React, { useContext, useState } from 'react';

import { createSearchParams, useNavigate } from 'react-router-dom';

import { ApiContext } from '_lib/api/apiContext';
import { Company } from '_lib/api/apiTypes.shim';
import { DialogActions } from '_lib/components/DialogActions/DialogActions';
import { DialogButton } from '_lib/components/DialogButton/DialogButton';
import { DialogContent } from '_lib/components/DialogContent/DialogContent';
import { MaskedPhoneInput } from '_lib/components/PhoneNumberTextField/PhoneNumberTextField';
import { SelectFilter } from '_lib/components/SelectFilter/SelectFilter';
import { Stack } from '_lib/components/Stack/Stack';
import { TextField } from '_lib/components/TextField/TextField';
import { Typography } from '_lib/components/Typography/Typography';
import { DialogWrapper } from '_lib/featureComponents/DialogWrapper/DialogWrapper';
import { appRoutePaths, getFullAppRoutePath } from '_lib/featureComponents/Nav/nav.types';
import { SearchSelect } from '_lib/featureComponents/SearchSelect/SearchSelect';
import { AsyncDialogProps, useAsyncDialog } from '_lib/hooks/useAsyncDialog';

type CallCustomerDialogProps = AsyncDialogProps;

const CallCustomerDialog = (props: CallCustomerDialogProps) => {
  const { onClose, titleText, ...dialogProps } = props;

  const navigate = useNavigate();

  const api = useContext(ApiContext);

  const [callCustomerState, setCallCustomerState] = useState<{
    callerId: string;
    company: Company | null;
    companyDNIS: string | null;
  }>({
    callerId: '',
    company: null,
    companyDNIS: null
  });

  const handleConfirm = () => {
    if (callCustomerState?.callerId.length !== 10 || !callCustomerState.company?.id || !callCustomerState.companyDNIS)
      return;

    const searchParamsString = createSearchParams({
      isManualCall: 'true',
      companyId: callCustomerState.company.id,
      durationStart: `${Date.now()}`
    }).toString();

    const url = `${getFullAppRoutePath(appRoutePaths.CallPop)}/${callCustomerState.companyDNIS}/${callCustomerState.callerId}?${searchParamsString}`;
    navigate(url);
  };

  return (
    <DialogWrapper dismissable={true} onClose={onClose} titleText={titleText || 'NEW CALL'} {...dialogProps}>
      <DialogContent sx={{ display: 'flex', minWidth: '450px', minHeight: '240px' }}>
        <Stack spacing={2.5} padding={2} useFlexGap sx={{ width: '100%' }}>
          <TextField
            required
            fullWidth
            label='Caller ID Phone Number'
            value={callCustomerState.callerId}
            onChange={(e) => setCallCustomerState((prev) => ({ ...prev, callerId: e.target.value }))}
            slotProps={{
              input: {
                inputComponent: MaskedPhoneInput
              }
            }}
          />
          <SearchSelect
            doSearch={async (searchValue) => {
              const res = await api.companies.list({ params: { searchWords: searchValue } });
              return res.results;
            }}
            selectedOption={callCustomerState.company}
            trySetSelectedOption={async (option) => {
              if (option) {
                const company = await api.companies.getById({ id: option.id });

                let companyDNIS = null;
                if (company.callCenter.dnisArray.length === 1 && company.callCenter.dnisArray[0]) {
                  companyDNIS = company.callCenter.dnisArray[0].phoneNumber;
                }

                setCallCustomerState((prev) => ({ ...prev, company, companyDNIS }));
                return true;
              }
              setCallCustomerState((prev) => ({ ...prev, company: null, companyDNIS: null }));
              return false;
            }}
            getSelectedOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
            renderListOption={(company, liProps) => {
              return (
                <li {...liProps} key={company.id}>
                  <Typography text={company.name} />
                </li>
              );
            }}
            label='Company'
            placeHolder='Company Name'
            required
            sx={{
              '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
                paddingTop: '3px',
                paddingBottom: '3px'
              },
              '& .MuiFormControl-root.MuiTextField-root': {
                marginY: 0
              }
            }}
          />
          {callCustomerState.company && (
            <SelectFilter
              fullWidth
              required
              label='Company Phone Number'
              initialKey={callCustomerState.companyDNIS || undefined}
              options={callCustomerState.company.callCenter.dnisArray.map((dnisItem) => dnisItem.phoneNumber) || []}
              enableEmptyValue
              usePhoneMask
              onChange={(companyDNIS) => setCallCustomerState((prev) => ({ ...prev, companyDNIS }))}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <DialogButton color='cancel' onClick={() => onClose({ action: 'CANCEL' })} text='Cancel' />
        <DialogButton
          onClick={handleConfirm}
          text='Confirm'
          disabled={
            callCustomerState?.callerId.length !== 10 ||
            !callCustomerState.company?.id ||
            !callCustomerState.companyDNIS
          }
        />
      </DialogActions>
    </DialogWrapper>
  );
};

export const useCallCustomerDialog = () => useAsyncDialog(CallCustomerDialog);
